import {
  Discount,
  VoyagersMemberInfo,
  IPriceToBookResponse,
} from '../../../../interfaces/src/lib/interfaces/index';
import { AppDispatch, CabinSelectionsType, GetLabels, store } from '../../index';
import {
  setServiceLabels,
  setEuropins010Toaster,
  removeInsurance,
  setIsProcessingEuropins010Warning,
} from '../stateManager/slices/bookingStepsSlice';
import {
  AgeRule,
  AgeRuleType,
  GuestData,
  ItineraryDetailsData,
  PromotionsPropsResponse,
  PromotionsState,
  GenericToaster,
  InsuranceItem,
} from '../../../../interfaces/src/lib/types/index';
import React, { SetStateAction } from 'react';
import {
  BookinFunnelLabelsEnum,
  CreateBookingWarningsEnum,
} from '../../../../interfaces/src/lib/enums/index';

export const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

export const truncateString = (text: string, maxLength: number | undefined): string => {
  if (maxLength && text.length > maxLength) {
    return text.slice(0, maxLength) + '...';
  }
  return text;
};

export const getItinaryOverrittenValue = (
  itineraryDetailsData: ItineraryDetailsData,
  setItineraryDescription: React.Dispatch<SetStateAction<string>>,
  maximumNumberOfTitleChars: number | undefined,
  dispatch: AppDispatch
) => {
  if (itineraryDetailsData?.code && !itineraryDetailsData?.errorId) {
    const state = store.getState();
    const serviceLabels = (state.bookingStepsStore?.serviceLabels || []).filter(
      (label, index, self) =>
        index === self.findIndex((l) => l.code === label.code && l.type === label.type)
    );
    // Ensure both code and type match
    const labelFromRedux = serviceLabels.find(
      (label) =>
        label.code === itineraryDetailsData.code && label.type === BookinFunnelLabelsEnum.ITIN
    );

    if (labelFromRedux) {
      setItineraryDescription(labelFromRedux.description);
      return;
    }

    const getLabels = new GetLabels(false, {
      type: BookinFunnelLabelsEnum.ITIN,
      codes: itineraryDetailsData.code,
    });
    getLabels
      .getData()
      .then((res) => {
        if (res?.data?.length > 0) {
          const itineraryDataLabels = res.data[0];
          console.log('itineraryData', itineraryDataLabels?.description);
          setItineraryDescription(itineraryDataLabels?.description || '');
          dispatch(
            setServiceLabels([
              ...serviceLabels,
              {
                code: itineraryDetailsData.code,
                type: BookinFunnelLabelsEnum.ITIN,
                description: itineraryDataLabels?.description || '',
                longDescription: '',
              },
            ])
          );
        } else {
          setItineraryDescription(
            truncateString(itineraryDetailsData?.description || '', maximumNumberOfTitleChars) || ''
          );
          dispatch(
            setServiceLabels([
              ...serviceLabels,
              {
                code: itineraryDetailsData.code,
                type: BookinFunnelLabelsEnum.ITIN,
                description: itineraryDetailsData.description,
                longDescription: '',
              },
            ])
          );
        }
      })
      .catch((error) => {
        console.info(error.message);
      });
  }
};

export const getAgeOnEmbarkationDate = (dateOfBirth: string, embarkationDate: string): number => {
  //calculate the age of the user on the embarkation date
  const dob = new Date(dateOfBirth);
  const embarkation = new Date(embarkationDate);
  let age = embarkation.getFullYear() - dob.getFullYear();

  // Adjust if the embarkation date is before the birthday in the current year
  if (
    embarkation.getMonth() < dob.getMonth() ||
    (embarkation.getMonth() === dob.getMonth() && embarkation.getDate() < dob.getDate())
  ) {
    age--;
  }
  return age;
};

/**
 * Retrieves the passenger data (if present in Redux store) and adds the new passengers data when
 * you go back to the occupancy step (from the checkout step) and increase the occupancy.
 */
export const getPassengersData = (
  passengersData: GuestData[] | undefined,
  cabinIndex: number,
  adultsOccupancy: number,
  childrenOccupancy: number,
  infantOccupancy: number,
  promotionAges: {
    adultAge: number | undefined;
    minorAge: number | undefined;
    infantAge: number | undefined;
  } | null
): { adultsSaved: GuestData[]; minorsSaved: GuestData[]; infantsSaved: GuestData[] } => {
  let adultsSaved: GuestData[] = [];
  let minorsSaved: GuestData[] = [];
  let infantsSaved: GuestData[] = [];

  if (passengersData?.length) {
    adultsSaved = passengersData.filter(
      (passenger) => passenger.cabinIndex === cabinIndex && passenger.type === 'adult'
    );
    minorsSaved = passengersData.filter(
      (passenger) => passenger.cabinIndex === cabinIndex && passenger.type === 'minor'
    );
    infantsSaved = passengersData.filter(
      (passenger) => passenger.cabinIndex === cabinIndex && passenger.type === 'infant'
    );

    while (adultsSaved.length < adultsOccupancy) {
      adultsSaved.push({ age: promotionAges?.adultAge || 30 });
    }

    while (minorsSaved.length < childrenOccupancy) {
      minorsSaved.push({ age: promotionAges?.minorAge || 10 });
    }

    while (infantsSaved.length < infantOccupancy) {
      infantsSaved.push({ age: promotionAges?.infantAge || 1 });
    }
  }

  return { adultsSaved, minorsSaved, infantsSaved };
};

/**
 * Retrieves an array of discount codes based on the cabin selections.
 * IMPORTANT: This function must be used only in the checkout page.
 *
 * @param {CabinSelectionsType} firstCabinSelections - The selections for the first cabin.
 * @param secondCabinSelections
 * @param {boolean} isApplicable - Flag indicating if the discount is applicable.
 * @param forceDiscounts - Flag used to force the discounts to be applied in prices and bookings API
 * @returns {Discount[]} An array of discount codes.
 */
export const getDiscountsArray = (
  firstCabinSelections: CabinSelectionsType,
  secondCabinSelections: CabinSelectionsType,
  isApplicable: boolean | undefined,
  forceDiscounts = false //this flag is used to force the discounts to be applied in prices and bookings API
): {
  discountCodesArrayFirstCabin: Discount[];
  discountCodesArraySecondCabin: Discount[];
} => {
  const firstCabinDiscountsToBeApplied =
    firstCabinSelections?.discountsToBeApplied?.discounts?.map((discount) => ({
      code: discount.code,
    })) ?? [];
  let discountCodesFirstCabin = [];
  let discountCodesSecondCabin = [];

  if (firstCabinSelections?.selectedCabinPreferenceObj?.cabinPreference === 'REAL') {
    discountCodesFirstCabin =
      (firstCabinDiscountsToBeApplied?.length && firstCabinDiscountsToBeApplied) ||
      firstCabinSelections?.selectedCategory?.discounts?.map((discount) => ({
        code: discount.code,
      })) ||
      [];

    discountCodesSecondCabin =
      secondCabinSelections?.selectedCategory?.discounts?.map((discount) => ({
        code: discount.code,
      })) || [];
  } else {
    discountCodesFirstCabin =
      (firstCabinDiscountsToBeApplied?.length && firstCabinDiscountsToBeApplied) ||
      firstCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.discounts?.map(
        (discount) => ({ code: discount.code })
      ) ||
      [];

    discountCodesSecondCabin =
      secondCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.discounts?.map(
        (discount) => ({ code: discount.code })
      ) || [];
  }

  const discountCodesArrayFirstCabin: Discount[] =
    discountCodesFirstCabin &&
    discountCodesFirstCabin.length > 0 &&
    (isApplicable || forceDiscounts)
      ? [...discountCodesFirstCabin]
      : [];
  const discountCodesArraySecondCabin: Discount[] =
    discountCodesSecondCabin &&
    discountCodesSecondCabin.length > 0 &&
    (isApplicable || forceDiscounts)
      ? [...discountCodesSecondCabin]
      : [];

  return {
    discountCodesArrayFirstCabin,
    discountCodesArraySecondCabin,
  };
};

/**
 * Checks if there is a Voyager's Club member present in the current cabin and returns their membership information.
 *
 * @param {CabinSelectionsType} currentCabin - The current cabin selections.
 * @returns {{ number: string, dateOfBirth: string } | null} The membership object containing the member's number and date of birth, or null if no member is present.
 */
export const checkAndAddMembershipObj = (
  currentCabin: CabinSelectionsType
): { number: string; dateOfBirth: string } | null => {
  const isVoyagersClubPresent = currentCabin?.voyagersMembersInfo?.length > 0;

  let membershipObj: { number: string; dateOfBirth: string } | null = null;
  if (isVoyagersClubPresent) {
    membershipObj = {
      number: currentCabin?.voyagersMembersInfo?.[0]?.memberInfo?.membership?.number || '',
      dateOfBirth: currentCabin?.voyagersMembersInfo?.[0]?.memberInfo?.dateOfBirth || '',
    };
  }

  return membershipObj;
};

/**
 * Retrieves the best Voyager's Club member based on their membership card type.
 * The hierarchy of membership card types is defined in the `voyagersMapping` static array.
 *
 * @returns The best Voyager's Club member object or null if no members are present.
 */
export const getBestVoyagersClubMember = () => {
  const state = store.getState();
  const voyagersMapping = ['Blue Diamond', 'Diamond', 'Gold', 'Silver', 'Classic', 'Welcome'];

  const voyagersMembersInfo =
    state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo || [];
  if (voyagersMembersInfo.length === 0) return null;

  return voyagersMembersInfo.reduce((best, current) => {
    const bestIndex = voyagersMapping.indexOf(best?.memberInfo?.membership?.cardType || '');
    const currentIndex = voyagersMapping.indexOf(current?.memberInfo?.membership?.cardType || '');
    return currentIndex < bestIndex ? current : best;
  });
};

export const manipulatePromotionsObj = (promotions: PromotionsPropsResponse): PromotionsState => {
  return {
    id: promotions.id,
    promotionName: promotions.promotionName || '',
    isApplicable: true,
    ...((promotions?.minimumOccupancyRule || promotions?.maximumOccupancyRule) && {
      occupancyRule: {
        ...(promotions?.minimumOccupancyRule && {
          minAdults: Number(promotions?.minimumOccupancyRule?.split('|')[0]),
          minMinors: Number(promotions?.minimumOccupancyRule?.split('|')[1]),
          minInfants: Number(promotions?.minimumOccupancyRule?.split('|')[2]),
        }),
        ...(promotions?.maximumOccupancyRule && {
          maxAdults: Number(promotions?.maximumOccupancyRule?.split('|')[0]),
          maxMinors: Number(promotions?.maximumOccupancyRule?.split('|')[1]),
          maxInfants: Number(promotions?.maximumOccupancyRule?.split('|')[2]),
        }),
        ...(promotions?.warningTitleOccupancy && {
          warningTitleOccupancy: {
            html: promotions?.warningTitleOccupancy?.html,
            plaintext: promotions?.warningTitleOccupancy?.plaintext,
          },
        }),
        ...(promotions?.warningDescriptionOccupancy && {
          warningDescriptionOccupancy: {
            html: promotions?.warningDescriptionOccupancy?.html,
            plaintext: promotions?.warningDescriptionOccupancy?.plaintext,
          },
        }),
        ...(promotions?.closesOnXOccupancy && {
          closesOnXOccupancy: promotions?.closesOnXOccupancy,
        }),
        ...(promotions?.disableAutoclosingOccupancy && {
          disableAutoclosingOccupancy: promotions?.disableAutoclosingOccupancy,
        }),
        ...(promotions?.closingTimeOccupancy && {
          closingTimeOccupancy: promotions?.closingTimeOccupancy,
        }),
        isApplicable: true,
      },
    }),
    ...((promotions.adultAgeRule || promotions.minorAgeRule || promotions.juniorAgeRule) && {
      ageRule: {
        ...(promotions.adultAgeRule &&
          promotions.adultAge && {
            ...(promotions.adultAgeRule && { adultAgeRule: promotions.adultAgeRule }),
            ...(promotions.adultAge && { adultAge: promotions.adultAge?.map(Number) }),
          }),
        ...(promotions.minorAgeRule &&
          promotions.minorAge && {
            ...(promotions.minorAgeRule && { minorAgeRule: promotions.minorAgeRule }),
            ...(promotions.minorAge && { minorAge: promotions.minorAge?.map(Number) }),
          }),
        ...(promotions.juniorAgeRule &&
          promotions.juniorAge && {
            ...(promotions.juniorAgeRule && { infantAgeRule: promotions.juniorAgeRule }),
            ...(promotions.juniorAge && { infantAge: promotions.juniorAge?.map(Number) }),
          }),
        ...(promotions?.warningTitleAge && {
          warningTitleAge: {
            html: promotions?.warningTitleAge?.html,
            plaintext: promotions?.warningTitleAge?.plaintext,
          },
        }),
        ...(promotions?.warningDescriptionAge && {
          warningDescriptionAge: {
            html: promotions?.warningDescriptionAge?.html,
            plaintext: promotions?.warningDescriptionAge?.plaintext,
          },
        }),
        ...(promotions?.closesOnXAge && { closesOnXAge: promotions?.closesOnXAge }),
        ...(promotions?.disableAutoclosingAge && {
          disableAutoclosingAge: promotions?.disableAutoclosingAge,
        }),
        ...(promotions?.closingTimeAge && { closingTimeAge: promotions?.closingTimeAge }),
        isApplicable: true,
      },
    }),
    ...((promotions.startDate || promotions.endDate) && {
      periodsRule: {
        ...(promotions.periodLabel && { periodLabel: promotions.periodLabel }),
        ...(promotions.startDate && { startDate: promotions.startDate }),
        ...(promotions.endDate && { endDate: promotions.endDate }),
        ...(promotions?.warningTitlePeriod && {
          warningTitlePeriod: {
            html: promotions?.warningTitlePeriod?.html,
            plaintext: promotions?.warningTitlePeriod?.plaintext,
          },
        }),
        ...(promotions?.warningDescriptionPeriod && {
          warningDescriptionPeriod: {
            html: promotions?.warningDescriptionPeriod?.html,
            plaintext: promotions?.warningDescriptionPeriod?.plaintext,
          },
        }),
        ...(promotions?.closesOnXPeriod && { closesOnXPeriod: promotions?.closesOnXPeriod }),
        ...(promotions?.disableAutoclosingPeriod && {
          disableAutoclosingPeriod: promotions?.disableAutoclosingPeriod,
        }),
        ...(promotions?.closingTimePeriod && { closingTimePeriod: promotions?.closingTimePeriod }),
        isApplicable: true,
      },
    }),
    ...(Boolean(promotions?.isVoyagersClubPromotion) && {
      voyagersClubRule: {
        ...(promotions?.warningTitleVC && {
          warningTitleVC: {
            html: promotions?.warningTitleVC?.html,
            plaintext: promotions?.warningTitleVC?.plaintext,
          },
        }),
        ...(promotions?.warningDescriptionVC && {
          warningDescriptionVC: {
            html: promotions?.warningDescriptionVC?.html,
            plaintext: promotions?.warningDescriptionVC.plaintext,
          },
        }),
        ...(promotions?.closesOnXVC && { closesOnXVC: promotions.closesOnXVC }),
        ...(promotions?.disableAutoclosingVC && {
          disableAutoclosingVC: promotions?.disableAutoclosingVC,
        }),
        ...(promotions?.closingTimeVC && { closingTimeVC: promotions.closingTimeVC }),
        isApplicable: true,
      },
    }),
  };
};

export const checkAgeRule = (ageRule: string, ageArray: number[], guestAge: number): boolean => {
  if (ageRule === AgeRuleType.OVER) {
    //check if the guest age is higher than the first element of the array
    return guestAge > ageArray[0];
  } else if (ageRule === AgeRuleType.UNDER) {
    //check if the guest age is lower than the first element of the array
    return guestAge < ageArray[0];
  } else {
    // RANGE scenario
    //Check if the guest age is the range between the first and the second element of the array
    return guestAge >= ageArray[0] && guestAge <= ageArray[1];
  }
};

/**
 * Retrieves the adjusted ages for different guest types based on the provided age rules and adjusts the age based on the rule type.
 *
 * @param {AgeRule | undefined} ageRules - The age rules to be applied.
 * @returns An object containing the adjusted ages for adults, minors, and infants, or null if no age rules are provided.
 */
export const getPromotionAges = (ageRules: AgeRule | undefined) => {
  if (ageRules) {
    const getAdjustedAge = (rule: string, ages: number[]) =>
      rule === AgeRuleType.OVER || rule === AgeRuleType.RANGE ? ages[0] + 1 : ages[0] - 1;

    return {
      adultAge:
        ageRules.adultAgeRule && ageRules.adultAge
          ? getAdjustedAge(ageRules.adultAgeRule, ageRules.adultAge)
          : undefined,
      minorAge:
        ageRules.minorAgeRule && ageRules.minorAge
          ? getAdjustedAge(ageRules.minorAgeRule, ageRules.minorAge)
          : undefined,
      infantAge:
        ageRules.infantAgeRule && ageRules.infantAge
          ? getAdjustedAge(ageRules.infantAgeRule, ageRules.infantAge)
          : undefined,
    };
  }
  return null;
};

/**
 * Handles the EUROPINS010 warning (unable_to_retrieve_insurance_price) from pricetobook response
 * This function should be called after every pricetobook API call to check for the warning
 * and manage the toaster display and insurance availability
 *
 * @param res - The pricetobook response
 * @param dispatch - Redux dispatch function
 * @param toasterConfig - The toaster configuration from AEM Foundations (europins010Toaster prop)
 * @param selectedInsurance - Currently selected insurances
 * @param allInsuranceLabels - All available insurance labels
 * @returns Set of unavailable insurance codes, or null if no warning
 */
export function handleEuropins010Warning(
  res: IPriceToBookResponse,
  dispatch: AppDispatch,
  toasterConfig: GenericToaster | undefined,
  selectedInsurance: InsuranceItem[] | undefined
): Set<string> | null {
  // Check for warning code unable_to_retrieve_insurance_price
  if (res?._warnings && res._warnings.length > 0) {
    const insurancePriceWarning = res._warnings.find(
      (warning) => warning.code === CreateBookingWarningsEnum.EUROPINS_010
    );

    if (insurancePriceWarning && toasterConfig?.toasterMessage) {
      // Set flag to prevent triggering pricetobook API when removing insurances
      dispatch(setIsProcessingEuropins010Warning(true));

      // Extract insurance codes from priceBreakdown (only those with type === 'INS')
      const availableInsuranceCodes = new Set(
        res.priceBreakdown?.filter((item) => item.type === 'INS').map((item) => item.code) || []
      );

      // Find all insurance codes that are not available in the response
      const unavailableCodes = new Set<string>();
      // Remove selected insurances that are not available in the response
      // This prevents them from being sent in subsequent API calls
      selectedInsurance?.forEach((insurance) => {
        if (!availableInsuranceCodes.has(insurance.code)) {
          dispatch(removeInsurance(insurance.code));
          unavailableCodes.add(insurance.code);
        }
      });

      // Set toaster configuration in Redux store
      dispatch(setEuropins010Toaster(toasterConfig));

      return unavailableCodes;
    }
  }

  // Clear toaster if no warning
  dispatch(setEuropins010Toaster(null));
  dispatch(setIsProcessingEuropins010Warning(false));
  return null;
}

export function checkVoyagersClubPromotion(
  isDeal: boolean,
  promotionObj: PromotionsState | null,
  voyagersMembersInfo: VoyagersMemberInfo[]
) {
  return !(
    !promotionObj ||
    (isDeal &&
      promotionObj?.voyagersClubRule &&
      (!voyagersMembersInfo || voyagersMembersInfo.length === 0))
  );
}

/**
 * Determines the next step after guest-info based on insurance availability.
 * If there's a dedicated insurance step but no products are available, skips to checkout.
 *
 * @param hasInsuranceProducts - Whether there are insurance products available
 * @returns The path to the next step: '/insurance' if products are available, '/checkout' otherwise
 */
export const getNextStepAfterGuestInfoWithInsuranceCheck = (
  hasInsuranceProducts: boolean
): string => {
  const state = store.getState();
  const hasInsuranceStep = state?.bookingStepsStore?.hasInsuranceStepConfigured ?? false;

  // If there's no insurance step in AEM, go to checkout
  if (!hasInsuranceStep) {
    return '/checkout';
  }

  // If there's an insurance step but no products, skip to checkout
  if (!hasInsuranceProducts) {
    return '/checkout';
  }

  // There's an insurance step and products are available
  return '/insurance';
};
